import { Capacitor } from '@capacitor/core';

// Pilot build only. No storage, timers, SQL text, arguments, IDs or error messages.
export const NATIVE_PERF_TRACE_ENABLED = import.meta.env.VITE_NATIVE_PERF_TRACE === '1'
  && Capacitor.isNativePlatform();
const MAX_SPANS = 128;
const PRELUDE_MS = 10_000;
const MAX_PAYLOAD_BYTES = 15 * 1024; // Leave room below the pilot's 16 KiB payload ceiling.
const SQL_METHODS = new Set(['query', 'run', 'execute', 'executeSet', 'executeTransaction']);
const READ_LABELS = new Set([
  'master:foods:all', 'master:businesses', 'master:shipments',
  'event:stock_in', 'event:stock_out', 'event:stock_reserved',
]);
type Outcome = 'ok' | 'error' | 'timeout';
type ReadPhase = 'db_open' | 'query' | 'parse_sort' | 'empty_diagnostics';
interface Span {
  id: number;
  kind: 'operation' | 'read';
  label: string;
  queued: number;
  started: number | null;
  ended: number | null;
  outcome: Outcome | null;
  readId?: number;
  phases?: Partial<Record<ReadPhase, number>>;
  phaseStarted: number;
}
const spans: Span[] = [];
let nextId = 0;
let nextVisit = 0;
let dropped = 0;
let invokingRead: number | undefined;

function beginSpan(kind: Span['kind'], label: string): Span {
  const now = performance.now();
  const span: Span = {
    id: ++nextId, kind, label, queued: now, started: kind === 'read' ? now : null,
    ended: null, outcome: null, phaseStarted: now,
  };
  if (kind === 'operation' && invokingRead !== undefined) span.readId = invokingRead;
  if (spans.length === MAX_SPANS) {
    // Preserve the gate reads and their SELECTs ahead of unrelated background work.
    const unrelated = spans.findIndex((item) => item.kind === 'operation' && item.readId === undefined);
    const completed = spans.findIndex((item) => item.ended !== null);
    spans.splice(unrelated >= 0 ? unrelated : completed >= 0 ? completed : 0, 1);
    dropped++;
  }
  spans.push(span);
  return span;
}

export function beginNativePerfOperation(method: string): Span | null {
  return NATIVE_PERF_TRACE_ENABLED && SQL_METHODS.has(method) ? beginSpan('operation', method) : null;
}

export function startNativePerfOperation(span: Span | null): void {
  if (span) span.started = performance.now();
}

export function finishNativePerfSpan(span: Span | null, outcome: Outcome): void {
  if (!span || span.ended !== null) return;
  span.ended = performance.now();
  span.outcome = outcome;
}

export function beginNativePerfRead(label: string): Span | null {
  return NATIVE_PERF_TRACE_ENABLED && READ_LABELS.has(label) ? beginSpan('read', label) : null;
}

export function markNativePerfRead(span: Span | null, phase: ReadPhase): void {
  if (!span) return;
  const now = performance.now();
  span.phases ??= {};
  span.phases[phase] = now - span.phaseStarted;
  span.phaseStarted = now;
}

/** db.query enqueues synchronously. Never retain a global label across await. */
export function invokeNativePerfRead<T>(span: Span | null, invoke: () => T): T {
  if (!span) return invoke();
  const previous = invokingRead;
  invokingRead = span.id;
  try { return invoke(); } finally { invokingRead = previous; }
}

export const WAREHOUSE_GATE_LABELS = ['foods', 'businesses', 'stock_in', 'stock_out', 'stock_reserved', 'shipments'] as const;
export const WAREHOUSE_GATE_READY = (1 << WAREHOUSE_GATE_LABELS.length) - 1;
interface WarehouseVisit {
  id: number;
  started: number;
  epoch: number;
  readyMask: number;
  errorMask: number;
  online: boolean;
  dropped: number;
  dataReady: number | null;
  finished: boolean;
}
export type WarehouseTraceOutcome = 'candidate_frame' | 'read_error' | 'left_or_scope_changed' | 'hidden' | 'operator_locked' | 'timeout';

export function beginWarehousePerfVisit(readyMask: number, errorMask: number): WarehouseVisit | null {
  if (!NATIVE_PERF_TRACE_ENABLED) return null;
  return {
    id: ++nextVisit, started: performance.now(), epoch: Date.now(), readyMask, errorMask,
    online: navigator.onLine, dropped, dataReady: null, finished: false,
  };
}

export function markWarehousePerfDataReady(visit: WarehouseVisit | null): void {
  if (visit && !visit.finished && visit.dataReady === null) visit.dataReady = performance.now();
}

const ms = (value: number): number => Math.round(value * 10) / 10;

/** Snapshot before dbg.info: that logger itself performs local DB operations. */
export function finishWarehousePerfVisit(visit: WarehouseVisit | null, outcome: WarehouseTraceOutcome) {
  if (!visit || visit.finished) return null;
  visit.finished = true;
  const ended = performance.now();
  const report = {
    schema: 1,
    visit: visit.id,
    visit_kind: visit.id === 1 ? 'first_in_process' : 'warm_or_resume',
    entry_epoch_ms: visit.epoch,
    process_uptime_ms: ms(visit.started),
    online_at_entry: visit.online,
    outcome,
    observed_to_end_ms: ms(ended - visit.started),
    data_ready_ms: visit.dataReady === null ? null : ms(visit.dataReady - visit.started),
    gate_at_entry: Object.fromEntries(WAREHOUSE_GATE_LABELS.map((label, index) => [
      label, visit.readyMask & (1 << index) ? 'cached' : visit.errorMask & (1 << index) ? 'error' : 'missing',
    ])),
    dropped_spans_during_visit: dropped - visit.dropped,
    dropped_spans_before_visit: visit.dropped,
    omitted_spans_for_size: 0,
    buffer_limit: MAX_SPANS,
    prelude_ms: PRELUDE_MS,
    spans: spans.filter((span) => span.ended === null || span.ended >= visit.started - PRELUDE_MS).map((span) => ({
      id: span.id,
      kind: span.kind,
      label: span.label,
      ...(span.readId === undefined ? {} : { read_id: span.readId }),
      queued_ms: ms(span.queued - visit.started),
      started_ms: span.started === null ? null : ms(span.started - visit.started),
      ended_ms: span.ended === null ? null : ms(span.ended - visit.started),
      outcome: span.outcome ?? 'pending',
      ...(span.kind === 'operation' ? {
        queue_wait_ms: span.started === null ? null : ms(span.started - span.queued),
        // Includes bridge, native work and delivery back to JS; never SQLite CPU.
        plugin_roundtrip_ms: span.started === null || span.ended === null || span.outcome === 'timeout'
          ? null : ms(span.ended - span.started),
        operation_elapsed_ms: span.started === null || span.ended === null ? null : ms(span.ended - span.started),
      } : {
        phases_ms: Object.fromEntries(Object.entries(span.phases ?? {}).map(([phase, duration]) => [phase, ms(duration)])),
        total_ms: span.ended === null ? null : ms(span.ended - span.queued),
      }),
    })),
  };
  // All fields/labels are ASCII allowlisted metadata, so length equals UTF-8 bytes.
  while (JSON.stringify(report).length > MAX_PAYLOAD_BYTES && report.spans.length) {
    const unrelated = report.spans.findIndex((span) => span.kind === 'operation' && !('read_id' in span));
    report.spans.splice(unrelated >= 0 ? unrelated : 0, 1);
    report.omitted_spans_for_size++;
  }
  return report;
}
